// Shared helpers for the audit viewer: timestamp formatting, action
// tone-color mapping, and ciphertext placeholder rendering so opaque
// bytea columns never surface as if they were readable PII values.

export const ALL_ACTIONS = [
  "INSERT",
  "UPDATE",
  "DELETE",
  "READ",
  "REVEAL",
  "EXPORT",
  "SIGN_IN",
  "SIGN_IN_FAILED",
  "LOCKOUT",
  "PIN_RESET",
  "DEVICE_ENROLLED",
  "DEVICE_REVOKED",
  "PERMISSION_DENIED",
];

export function actionTone(action: string): string {
  if (
    action === "SIGN_IN_FAILED" ||
    action === "PERMISSION_DENIED" ||
    action === "LOCKOUT" ||
    action === "DELETE"
  ) {
    return "bg-red-500";
  }
  if (action === "DEVICE_REVOKED" || action === "PIN_RESET") {
    return "bg-amber-500";
  }
  if (action === "REVEAL" || action === "EXPORT") {
    return "bg-indigo-500";
  }
  if (action === "SIGN_IN" || action === "DEVICE_ENROLLED" || action === "INSERT") {
    return "bg-emerald-500";
  }
  return "bg-slate-400";
}

export function formatCt(iso: string): string {
  // America/Chicago display.
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      timeZone: "America/Chicago",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return iso;
  }
}

// Replace any string on a *_ct, *_bidx, pin_hash, or credential_hash key
// with a human-readable placeholder. Keeps opaque ciphertext from looking
// like a visible column and reinforces to the Owner that the system is
// NOT showing a raw PII value — just acknowledging an encrypted field.
export function renderBytea(value: unknown): unknown {
  if (value == null) return value;
  if (Array.isArray(value)) return value.map(renderBytea);
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (/_ct$|_bidx$|pin_hash|credential_hash/i.test(k) && typeof v === "string") {
        out[k] = `<ciphertext ${Math.ceil((v.length * 3) / 4)} bytes>`;
      } else {
        out[k] = renderBytea(v);
      }
    }
    return out;
  }
  return value;
}

export function ActionDot({ action }: { action: string }) {
  return (
    <span
      className={`h-2.5 w-2.5 rounded-full shrink-0 ${actionTone(action)}`}
      aria-hidden="true"
    />
  );
}
